/**
 * ClientMagicLinkModal — modal que el coach abre para generar un link de
 * acceso a la app móvil del cliente.
 *
 * Llama `POST /aetherlite/clients/{id}/magic-link` (backend ya existe). Muestra:
 *   - El token completo en una caja monospace + botón "Copiar".
 *   - Mensaje pre-armado listo para "Compartir por WhatsApp" (wa.me link).
 *   - Botón "Generar nuevo" para invalidar el actual y crear otro (idempotente —
 *     genera un token nuevo cada vez; el anterior queda válido hasta su TTL).
 *
 * UX intencional: NO mostramos el deep link `aetherlite://verify?token=...`
 * de forma prominente. El cliente que recibe el link suele estar en celu, y
 * los deep links son inestables (Android a veces no abre la app si está
 * cerrada). La estrategia robusta es: mandamos un mensaje con el código y
 * un link a la app store; el cliente abre la app, va a "Verify", pega el
 * código. Si el deep link funciona, mejor.
 */
import { useEffect, useState } from "react";
import { Icon } from "../../lib/Icon";
import { useToast } from "../../lib/toast";
import { generateClientMagicLink, type ClientFull, type ClientMagicLink } from "../../api/clients";

interface ClientMagicLinkModalProps {
  client: ClientFull;
  onClose: () => void;
}

export function ClientMagicLinkModal({ client, onClose }: ClientMagicLinkModalProps) {
  const toast = useToast();
  const [link, setLink] = useState<ClientMagicLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await generateClientMagicLink(client.id);
      setLink(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Generamos automáticamente al abrir — el coach ya hizo el click "Generar"
  // que abrió este modal, no le pidamos otro.
  useEffect(() => {
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC cierra
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyToken = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.token);
      toast("Código copiado al portapapeles");
    } catch {
      toast("No pude copiar — copiá manualmente");
    }
  };

  /** Mensaje pre-armado para WhatsApp. Incluye el código + un texto humano
   *  explicando para qué sirve. URL-encoded. */
  const whatsAppHref = (() => {
    if (!link) return "";
    const phone = (client.phone || "").replace(/[^\d]/g, "");
    const first = (client.full_name || "").split(" ")[0] || "Hola";
    const msg = [
      `Hola ${first}!`,
      "",
      `Te invité a usar la app de AetherLite para ver tu plan y registrar tu progreso. Bajala desde la App Store o Google Play (buscá "AetherLite"), abrila, tocá "Pegar código" y usá este:`,
      "",
      link.token,
      "",
      `El código vence en ${link.expires_in_minutes} minutos.`,
    ].join("\n");
    const encoded = encodeURIComponent(msg);
    return phone ? `https://wa.me/${phone}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
  })();

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-cli-ml"
        role="dialog"
        aria-label="Link de acceso a la app móvil"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              📱 Link de acceso a la app
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Para que <b>{client.full_name}</b> entre a la app móvil sin password
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-cli-ml-body">
          {busy && !link && (
            <div className="muted" style={{ padding: 24, textAlign: "center" }}>
              Generando…
            </div>
          )}

          {err && (
            <div className="al-staff-login-err" style={{ marginBottom: 12 }}>
              {err}
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={generate}>
                  Reintentar
                </button>
              </div>
            </div>
          )}

          {link && (
            <>
              <div className="al-cli-ml-section">
                <div className="al-cli-ml-section-title">CÓDIGO DE UN SOLO USO</div>
                <div className="al-cli-ml-token-box">
                  <code className="al-cli-ml-token">{link.token}</code>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={copyToken}
                    title="Copiar al portapapeles"
                  >
                    Copiar
                  </button>
                </div>
                <div className="tiny muted">
                  Vence en <b>{link.expires_in_minutes} minutos</b>. Si vence, generá uno nuevo.
                </div>
              </div>

              <div className="al-cli-ml-section">
                <div className="al-cli-ml-section-title">COMPARTIR</div>
                <div className="al-cli-ml-share">
                  <a
                    className="btn btn-primary"
                    href={whatsAppHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="send" size={14} />
                    Enviar por WhatsApp
                    {!client.phone && (
                      <span className="tiny" style={{ marginLeft: 6, opacity: 0.85 }}>
                        (elegí contacto)
                      </span>
                    )}
                  </a>
                  {!client.phone && (
                    <div className="tiny muted" style={{ marginTop: 6 }}>
                      Sin teléfono cargado para este cliente — abrir WhatsApp te deja
                      elegir el contacto. Cargá el teléfono en "Identidad" para
                      que el link vaya directo.
                    </div>
                  )}
                </div>
              </div>

              <details className="al-cli-ml-advanced">
                <summary>Opciones avanzadas</summary>
                <div style={{ marginTop: 10 }}>
                  <div className="al-cli-ml-section-title">DEEP LINK (avanzado)</div>
                  <div className="al-cli-ml-token-box">
                    <code className="al-cli-ml-token al-cli-ml-token-small">
                      {link.deep_link}
                    </code>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(link.deep_link);
                          toast("Deep link copiado");
                        } catch {
                          toast("No pude copiar");
                        }
                      }}
                    >
                      Copiar
                    </button>
                  </div>
                  <div className="tiny muted">
                    Abre la app directo si está instalada. Funciona menos confiable
                    en Android — preferí mandar el código.
                  </div>
                </div>
              </details>
            </>
          )}
        </div>

        <div className="al-cli-ml-foot">
          <button className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
          <div style={{ flex: 1 }} />
          {link && (
            <button
              className="btn btn-secondary"
              onClick={generate}
              disabled={busy}
              title="Invalida el código actual generando uno nuevo (en realidad ambos quedan válidos hasta su TTL)"
            >
              {busy ? "Generando…" : "Generar nuevo código"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
